import React from 'react';
import {
    Modal, Box, SpaceBetween, Button, Alert, Link
} from '@amzn/awsui-components-react';
import { EntityType } from 'src/models/multidimensional-allocation/EntityType';
import { LinkedMultiAllocationEntity } from 'src/models/multidimensional-allocation/GetMultiAllocationLinkedEntitiesResponse';
import {getEntityLabel} from "src/components/multidimensional-allocation/common/Constants";

export interface LinkedEntitiesModalProps {
    /** Controls modal visibility */
    visible: boolean;
    /** The name of the entity (worksheet or sheet name) */
    entityName: string;
    /** Type of entity to determine labels */
    entityType: EntityType;
    /** Array of linked entities */
    linkedEntities: LinkedMultiAllocationEntity[];
    /** Count of linked entities */
    linkedCount: number;
    /** Callback to close modal */
    onDismiss: () => void;
}

/**
 * Generic modal component for displaying linked entities in multidimensional allocation.
 * Can be used for both worksheets (showing linked CLIs) and sheets (showing linked worksheets).
 *
 * @returns React functional component rendering linked entities modal
 */
export const LinkedEntitiesModal: React.FC<LinkedEntitiesModalProps> = ({
    visible,
    entityName,
    entityType,
    linkedEntities,
    linkedCount,
    onDismiss
}) => {
    const entityLabel = getEntityLabel(entityType, linkedCount);

    return (
        <Modal
            onDismiss={onDismiss}
            visible={visible}
            closeAriaLabel="Close modal"
            size="large"
            footer={
                <Box float="right">
                    <Button variant="primary" onClick={onDismiss}>
                        OK
                    </Button>
                </Box>
            }
            header={`View Linked ${entityLabel}`}
        >
            <SpaceBetween direction="vertical" size="l">
                {/* Warning/Info Alert Box */}
                <Alert type="warning" header={`${linkedCount} Linked ${entityLabel}`}>
                    <SpaceBetween direction="vertical" size="xs">
                        <Box>
                            <strong>{entityName}</strong> has <strong>{linkedCount}</strong> linked {entityLabel} (listed below).
                        </Box>
                    </SpaceBetween>
                </Alert>

                {/* Instruction Text */}
                <Box>
                    Click {entityLabel} to see details
                </Box>

                {/* Linked Entities Grid - 2 columns */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}>
                    {linkedEntities.map((entity: LinkedMultiAllocationEntity) => {
                        // Determine link and display text based on entity type
                        const isWorksheet = entityType === EntityType.MULTI_ALLOCATION_WORKSHEET;
                        const displayText = isWorksheet ? entity.entityId : entity.entityName;
                        const linkHref = isWorksheet
                            ? `/calculations/builder/${btoa(entity.entityRecordId)}`
                            : `/calculations/multidimensionalAllocation/calculation/${entity.entityId}`;
                        
                        return (
                            <Box key={entity.entityId}>
                                <Link
                                    variant="primary"
                                    href={linkHref}
                                    external
                                    externalIconAriaLabel="Opens in a new tab"
                                >
                                    {displayText}
                                </Link>
                            </Box>
                        );
                    })}
                </div>
            </SpaceBetween>
        </Modal>
    );
};
